import React, { useMemo, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button, SegmentedControl } from '../../../design-system';
import {
  EXPERIMENT_PREVIEW_CATALOG,
  clearExperimentPreviewOverrides,
  getExperimentPreviewOverride,
  setExperimentPreviewOverride,
} from '../../../utils/experimentPreviewOverrides';
import './ExperimentSettings.css';

const POSTHOG_ASSIGNMENT = 'posthog';

type ExperimentSelections = Record<string, string>;

const readSelections = (): ExperimentSelections =>
  EXPERIMENT_PREVIEW_CATALOG.reduce<ExperimentSelections>((selections, experiment) => {
    const validVariants = experiment.variants.map(({ value }) => value);
    selections[experiment.featureFlag] =
      getExperimentPreviewOverride(experiment.featureFlag, validVariants) || POSTHOG_ASSIGNMENT;
    return selections;
  }, {});

const getVariantControlLabel = (variant: string): string =>
  variant === 'control' ? 'Control' : 'Treatment';

const getStatusLabel = (status: string): string =>
  status === 'active' ? 'Running' : status.charAt(0).toUpperCase() + status.slice(1);

export default function ExperimentSettings(): React.ReactElement {
  const [selections, setSelections] = useState<ExperimentSelections>(readSelections);
  const [announcement, setAnnouncement] = useState('');
  const hasOverrides = useMemo(
    () => Object.values(selections).some((selection) => selection !== POSTHOG_ASSIGNMENT),
    [selections],
  );

  const handleSelectionChange = (
    featureFlag: string,
    label: string,
    validVariants: readonly string[],
    selection: string,
  ): void => {
    const variant = selection === POSTHOG_ASSIGNMENT ? null : selection;
    const didSave = setExperimentPreviewOverride(featureFlag, variant, validVariants);
    if (!didSave) {
      setAnnouncement(`${label} preview could not be saved.`);
      return;
    }

    setSelections((currentSelections) => ({
      ...currentSelections,
      [featureFlag]: selection,
    }));
    setAnnouncement(
      selection === POSTHOG_ASSIGNMENT
        ? `${label} now follows PostHog assignment.`
        : `${label} now uses ${getVariantControlLabel(selection)} in this browser session.`,
    );
  };

  const handleResetAll = (): void => {
    if (!clearExperimentPreviewOverrides()) {
      setAnnouncement('Experiment previews could not be reset.');
      return;
    }

    setSelections(readSelections());
    setAnnouncement('All experiments now follow PostHog assignment.');
  };

  return (
    <div className="experiment-settings">
      <div className="settings-subpage-header experiment-settings__header">
        <div className="settings-subpage-header-left">
          <h2 className="settings-subpage-title">Experiments</h2>
          <p className="settings-subpage-description">
            Preview presentation variants without changing PostHog traffic or results.
          </p>
        </div>
        {hasOverrides ? (
          <Button onClick={handleResetAll} size="sm" variant="outline">
            Use PostHog for all
          </Button>
        ) : null}
      </div>

      <p className="experiment-settings__scope">
        Changes stay in this browser session and work only on local and development hosts.
      </p>

      <section className="experiment-settings__list" aria-label="Experiment previews">
        {EXPERIMENT_PREVIEW_CATALOG.map((experiment) => {
          const validVariants = experiment.variants.map(({ value }) => value);
          const selection = selections[experiment.featureFlag] || POSTHOG_ASSIGNMENT;
          const selectedVariant = experiment.variants.find(({ value }) => value === selection);
          const selectionDescription = selectedVariant
            ? `Forced: ${selectedVariant.label}`
            : 'Assigned by PostHog';
          const headingId = `experiment-preview-${experiment.registryKey.toLowerCase()}`;
          const options = [
            { label: 'PostHog', value: POSTHOG_ASSIGNMENT },
            ...experiment.variants.map(({ value }) => ({
              label: getVariantControlLabel(value),
              value,
            })),
          ];

          return (
            <article
              aria-labelledby={headingId}
              className="experiment-settings__row"
              key={experiment.registryKey}
            >
              <div className="experiment-settings__identity">
                <div className="experiment-settings__title-line">
                  <h3 id={headingId}>{experiment.label}</h3>
                  <span>{getStatusLabel(experiment.status)}</span>
                </div>
                <p>{experiment.description}</p>
                <div className="experiment-settings__metadata">
                  <span>
                    {experiment.posthogExperimentId
                      ? `PostHog #${experiment.posthogExperimentId}`
                      : 'PostHog draft'}
                  </span>
                  <span>{selectionDescription}</span>
                </div>
              </div>

              <div className="experiment-settings__controls">
                <SegmentedControl
                  className="experiment-settings__segmented"
                  label={`Variant source for ${experiment.label}`}
                  onChange={(nextSelection) =>
                    handleSelectionChange(
                      experiment.featureFlag,
                      experiment.label,
                      validVariants,
                      nextSelection,
                    )
                  }
                  options={options}
                  value={selection}
                />
                <Button
                  as={Link}
                  className="experiment-settings__open-link"
                  iconAfter={<ArrowUpRight aria-hidden="true" size={15} />}
                  size="sm"
                  to={experiment.route}
                  variant="ghost"
                >
                  Open surface
                </Button>
              </div>
            </article>
          );
        })}
      </section>

      <p aria-live="polite" className="experiment-settings__announcement" role="status">
        {announcement}
      </p>
    </div>
  );
}
